'use client';

import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Dropdown, { type DropdownOption } from '@publishing/components/forms/dropdown/Dropdown';
import { SelectionList, SelectionListItem } from '@publishing/components/forms/SelectionList';
import { VARIANT_PRESETS } from '@publishing/components/forms/input/_types';
import Icon from '@publishing/components/ui/Icon';
import { ActionCloseType1, ArrowChevron, EtcClose } from '@publishing/components/ui/SvgSprite';
import { Button } from '@publishing/components/ui/Button';
import EventBubble from '@publishing/components/events/EventBubble';
import Badge from '@publishing/components/ui/Badge';
import Form from '../Form';
import {
  CONSULT_POPUP_MENUS,
  FORM_MENUS,
  MULTI_CONSULT_ONE_PERSON_KEYS,
  POPUP_FORM_NAME,
  POPUP_FORM_NAME_MULTI,
  POPUP_LEAD_TYPE,
} from '../_config';
import { getFormFieldPresetKey } from '../_types';
import type { FormAgreementOptions, FormField, FormMenuConfig, FormKey } from '../_types';
import styles from '../FormPopup.module.scss';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick, pushGTMEvent, toBtnName } from '@/shared/utils/gtm';
import type { ItemCategory } from '@/features/lead-form/utils/leadFormGtm';

const CONSULT_SELECTION_NAME = 'consult-menu';

/**
 * 선택한 상품들의 사업 종류(GA `item_category`) — 중복은 제거하고 선택 순서를 유지한다.
 *
 * 상품 하나가 곧 사업 라인 하나는 아니라서(공유·전용·맞춤형오피스는 모두 공유오피스)
 * N 개를 골라도 사업 종류는 그보다 적을 수 있다.
 */
function toItemCategories(menus: readonly FormMenuConfig[]): ItemCategory[] {
  return [
    ...new Set(
      menus
        .map((menu) => menu.itemCategory)
        .filter((v): v is ItemCategory => !!v),
    ),
  ];
}

const ONE_PERSON_MENU_KEYS = new Set<string>(MULTI_CONSULT_ONE_PERSON_KEYS);

type MultiSelectionMode = 'empty' | 'one-person-only' | 'mixed' | 'other-only';

function resolveInitialSelectedKeys(
  layout: ConsultLayout,
  initialKey: string,
  menus: FormMenuConfig[],
): Set<string> {
  if (layout === 'multi') {
    const k = initialKey && menus.some((m) => m.key === initialKey) ? initialKey : null;
    return new Set(k ? [k] : []);
  }
  const k = initialKey && menus.some((m) => m.key === initialKey) ? initialKey : menus[0]?.key;
  return new Set(k ? [k] : []);
}

function getMultiSelectionMode(selectedKeys: Set<string>): MultiSelectionMode {
  if (selectedKeys.size === 0) return 'empty';

  const keys = [...selectedKeys];
  const allOnePerson = keys.every((key) => ONE_PERSON_MENU_KEYS.has(key));
  if (allOnePerson) return 'one-person-only';

  const hasOnePerson = keys.some((key) => ONE_PERSON_MENU_KEYS.has(key));
  return hasOnePerson ? 'mixed' : 'other-only';
}

/** multi — 선택 모드 전환 시 userCount 동기화 (1인 전용만 → 1, 그 외 포함 → 빈값) */
function multiUserCountSyncOnModeChange(
  mode: MultiSelectionMode,
  prevMode: MultiSelectionMode,
): Partial<Record<string, string | undefined>> | undefined {
  if (mode === 'one-person-only' && prevMode !== 'one-person-only') {
    return { userCount: '1' };
  }
  if (prevMode === 'one-person-only' && mode !== 'one-person-only') {
    return { userCount: '' };
  }
  return undefined;
}

function initialMultiUserCountSync(selectedKeys: Set<string>): Partial<Record<string, string | undefined>> | undefined {
  if (getMultiSelectionMode(selectedKeys) === 'one-person-only') {
    return { userCount: '1' };
  }
  return undefined;
}

// ── 다중 선택 필드 병합 유틸 ──────────────────────────────

function fieldStableKey(field: FormField): string {
  if (field.name) return field.name;
  const pk = getFormFieldPresetKey(field);
  if (pk) return VARIANT_PRESETS[pk].name ?? pk;
  return '';
}

function effectiveFields(menu: FormMenuConfig): FormField[] {
  return menu.fields ?? FORM_MENUS[0]?.fields ?? [];
}

function menusInSelectionOrder(allMenus: FormMenuConfig[], selectedKeys: Set<string>): FormMenuConfig[] {
  return allMenus.filter((m) => selectedKeys.has(m.key));
}

function mergeFields(selectedMenus: FormMenuConfig[]): FormField[] {
  const seen = new Set<string>();
  const out: FormField[] = [];
  for (const menu of selectedMenus) {
    for (const field of effectiveFields(menu)) {
      const key = fieldStableKey(field);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(field);
    }
  }
  return out;
}

function mergeSubmitLabel(selectedMenus: FormMenuConfig[]): string {
  for (const m of selectedMenus) {
    if (m.submitLabel) return m.submitLabel;
  }
  return '문의하기';
}

function mergeCompleteKey(selectedMenus: FormMenuConfig[]): string {
  for (const m of selectedMenus) {
    if (m.completeKey) return m.completeKey;
  }
  return 'default';
}

/** tab 팝업 — 1인 전용 상품 탭: 예상 이용 인원 1명 고정(view) */
function withOnePersonTabUserCount(fields: FormField[]): FormField[] {
  return fields.map((field) => {
    if (fieldStableKey(field) !== 'userCount') return field;
    return { ...field, type: 'userCount', defaultValue: '1', view: true };
  });
}

// ── Props ─────────────────────────────────────────────────

export type ConsultLayout = 'tabs' | 'multi';

export interface FormConsultBodyProps {
  menus?: FormMenuConfig[];
  layout?: ConsultLayout;
  agreement?: FormAgreementOptions;
  /** 팝업을 연 CTA 의 리드폼 구분값 — 제출 이벤트로 이어 붙인다. */
  leadFormNum?: number;
  defaultMenuKey?: FormKey | '';
  onComplete: (meta: { completeKey: string }) => void;
  /** multi 모바일 시트의 닫기 — 팝업 자체를 닫는다 */
  onClose?: () => void;
}

// ── 컴포넌트 ──────────────────────────────────────────────

export default function FormConsultBody({
  menus = CONSULT_POPUP_MENUS,
  layout = 'tabs',
  agreement,
  leadFormNum,
  defaultMenuKey,
  onComplete,
  onClose,
}: FormConsultBodyProps) {
  const initialKey = defaultMenuKey ?? menus[0]?.key ?? '';

  // ── tabs: 단일 활성 메뉴 ────────────────────────────────
  const [activeMenuKey, setActiveMenuKey] = useState(
    () => (initialKey && menus.some((m) => m.key === initialKey) ? initialKey : menus[0]?.key) ?? '',
  );

  const activeMenu = useMemo(
    () => menus.find((m) => m.key === activeMenuKey) ?? menus[0],
    [menus, activeMenuKey],
  );

  const tabFormFields = useMemo(() => {
    if (layout !== 'tabs' || !activeMenu?.key) {
      return undefined;
    }
    if (!ONE_PERSON_MENU_KEYS.has(activeMenu.key)) {
      return undefined;
    }
    return withOnePersonTabUserCount(effectiveFields(activeMenu));
  }, [layout, activeMenu]);

  const menuDropdownOptions: DropdownOption[] = useMemo(
    () => menus.map((menu) => ({
      value: menu.key,
      label: menu.label,
      subtitle: menu.groupLabel,
      groupTitle: menu.groupLabel ?? '기타',
    })),
    [menus],
  );

  // ── multi: 선택 키 집합 ─────────────────────────────────
  const [selectedKeys, setSelectedKeys] = useState(() =>
    resolveInitialSelectedKeys(layout, initialKey, menus),
  );

  const groupedMenus = useMemo(() => {
    const groups: { label: string; menus: FormMenuConfig[] }[] = [];
    menus.forEach((menu) => {
      const label = menu.groupLabel ?? '기타';
      const last = groups[groups.length - 1];
      if (last?.label === label) last.menus.push(menu);
      else groups.push({ label, menus: [menu] });
    });
    return groups;
  }, [menus]);

  const selectedMenus = useMemo(
    () => menusInSelectionOrder(menus, selectedKeys),
    [menus, selectedKeys],
  );

  const mergedFields = useMemo(
    () => mergeFields(selectedMenus),
    [selectedMenus],
  );
  const submitLabelMulti = useMemo(() => mergeSubmitLabel(selectedMenus), [selectedMenus]);

  const [selectionMode, setSelectionMode] = useState<MultiSelectionMode>(() =>
    layout === 'multi' ? getMultiSelectionMode(resolveInitialSelectedKeys(layout, initialKey, menus)) : 'empty',
  );
  const [multiUserCountSync, setMultiUserCountSync] = useState<
    Partial<Record<string, string | undefined>> | undefined
  >(() => (layout === 'multi' ? initialMultiUserCountSync(resolveInitialSelectedKeys(layout, initialKey, menus)) : undefined));

  const applySelection = useCallback((nextKeys: Set<string>) => {
    if (nextKeys.size === 0) {
      setSelectionMode('empty');
      setSelectedKeys(nextKeys);
      setMultiUserCountSync(undefined);
      return;
    }

    const mode = getMultiSelectionMode(nextKeys);
    const prevMode = selectionMode;
    const userCountSync = multiUserCountSyncOnModeChange(mode, prevMode);

    if (userCountSync) {
      setMultiUserCountSync(userCountSync);
    }

    setSelectionMode(mode);
    setSelectedKeys(nextKeys);
  }, [selectionMode]);

  /** 선택 안내(CLICK 배지·말풍선) — 한 번 선택하거나 목록을 스크롤하면 해제, 팝업을 다시 열어야 복귀 */
  const [hintDismissed, setHintDismissed] = useState(false);
  const showHint = !hintDismissed && selectedKeys.size === 0;

  const toggleMenuKey = useCallback((menuKey: string, checked: boolean) => {
    setHintDismissed(true);
    const next = new Set(selectedKeys);
    if (checked) next.add(menuKey);
    else next.delete(menuKey);
    applySelection(next);

    // 데스크톱 aside·모바일 시트·선택 태그가 모두 이 함수를 거친다.
    // 해제는 '카테고리 선택'이 아니므로 체크 시에만 발사.
    if (!checked) return;
    const label = menus.find((m) => m.key === menuKey)?.label;
    if (label) {
      pushGTMEvent(GTM_EVENT.CATEGORY2, { btnname: toBtnName(label) });
    }
  }, [selectedKeys, applySelection, menus]);

  /** 모바일 첫 진입은 선택 시트부터 */
  const [mobilePickerOpen, setMobilePickerOpen] = useState(true);

  useEffect(() => {
    if (!mobilePickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedKeys.size !== 0) {
        setMobilePickerOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobilePickerOpen, selectedKeys]);

  const multiSummaryLabel = useMemo(() => {
    if (selectedMenus.length === 0) return '상품을 선택해주세요.';
    if (selectedMenus.length === 1) return selectedMenus[0].label;
    return `${selectedMenus[0].label} 외 ${selectedMenus.length - 1}개`;
  }, [selectedMenus]);

  const selectedValuesArray = useMemo(() => [...selectedKeys], [selectedKeys]);

  const renderMultiSelectionGroups = (namePrefix: string) =>
    groupedMenus.map((group, groupIndex) => (
      <section key={group.label} className={styles.selection_group}>
        <h4 className={styles.selection_group_title}>{group.label}</h4>
        <SelectionList
          type="checkbox"
          name={`${namePrefix}-${groupIndex}`}
          className={styles.selection_list}
          value={selectedValuesArray}
        >
          {groupIndex === 0 && (
            <li
              className={clsx(styles.selection_badge_item, !showHint && styles.selection_badge_item_out)}
              aria-hidden
            >
              <Badge size="medium" bgColor="--color-primary-600" className={styles.selection_badge}>
                CLICK
              </Badge>
            </li>
          )}
          {group.menus.map((menu, idx) => (
            <SelectionListItem
              key={menu.key}
              value={menu.key}
              label={menu.label}
              subLabel={menu.selectionCaption}
              checked={selectedKeys.has(menu.key)}
              className={clsx(
                groupIndex === 0 && idx === 0 && styles.selection_hint,
                groupIndex === 0 && idx === 0 && !showHint && styles.selection_hint_out,
              )}
              onChange={(e) => {
                const checked = (e.target as HTMLInputElement).checked;
                toggleMenuKey(menu.key, checked);
              }}
            />
          ))}
        </SelectionList>
      </section>
    ));

  // ── tabs layout ────────────────────────────────────────

  if (layout === 'tabs') {
    return (
      <div className={styles.body}>
        <aside className={styles.side_menu}>
          {groupedMenus.map((group) => (
            <section key={group.label} className={styles.menu_group}>
              <h4 className={styles.group_title}>{group.label}</h4>
              {group.menus.map((menu) => (
                <button
                  key={menu.key}
                  type="button"
                  onClick={() => setActiveMenuKey(menu.key)}
                  className={clsx(styles.menu_item, activeMenu?.key === menu.key && styles.menu_item_active)}
                  {...gtmClick(GTM_EVENT.CATEGORY2, menu.label)}
                >
                  {menu.label}
                  <Icon name={ArrowChevron} direction="right" size={20} className={styles.menu_chevron} />
                </button>
              ))}
            </section>
          ))}
        </aside>

        <div className={styles.mobile_menu}>
          <Dropdown
            label="상품 유형"
            options={menuDropdownOptions}
            value={activeMenuKey}
            onChange={(v) => {
              setActiveMenuKey(v as FormKey);
              // 드롭다운은 여는 클릭까지 잡히므로 data 어트리뷰트가 아닌 선택 시점에 push.
              const label = menus.find((m) => m.key === v)?.label;
              if (label) {
                pushGTMEvent(GTM_EVENT.CATEGORY2, { btnname: toBtnName(label) });
              }
            }}
            isSheet
            sheetHeaderType="custom"
            className={styles.mobile_menu_dropdown}
          />
        </div>

        <Form
          key={activeMenu?.key}
          formName={POPUP_FORM_NAME}
          leadFormNum={leadFormNum}
          leadType={POPUP_LEAD_TYPE}
          gtmItemCategory={toItemCategories(activeMenu ? [activeMenu] : [])}
          type={activeMenu?.key as FormKey}
          fields={tabFormFields}
          extraData={{ productType: activeMenu?.productTypeValue ? [activeMenu.productTypeValue] : [] }}
          agreement={agreement}
          className={styles.form_area}
          actionAreaClassName={styles.form_action_area}
          onSuccess={() => {
            onComplete({ completeKey: activeMenu?.completeKey ?? 'default' });
          }}
        />
      </div>
    );
  }

  // ── multi layout ───────────────────────────────────────

  const selectionAside = (
    <aside
      className={styles.consult_selection_panel}
      aria-label="상담 상품 선택"
      onScroll={(e) => {
        if (e.currentTarget.scrollTop > 0) setHintDismissed(true);
      }}
    >
      {renderMultiSelectionGroups(CONSULT_SELECTION_NAME)}
    </aside>
  );

  return (
    <div className={`${styles.body} ${styles.body_consult_multi}`}>
      <div className={styles.consult_multi_mobile}>
        <div className={styles.consult_multi_trigger_wrap}>
          <button
            type="button"
            className={styles.consult_multi_trigger}
            aria-expanded={mobilePickerOpen}
            aria-haspopup="dialog"
            onClick={() => setMobilePickerOpen(true)}
          >
            <span className={clsx(styles.consult_multi_trigger_value, selectedMenus.length === 0 && styles.consult_multi_trigger_placeholder)}>
              {multiSummaryLabel}
            </span>
            <Icon name={ArrowChevron} direction="down" size={24} />
          </button>
        </div>
        {selectedMenus.length > 0 && (
          <ul className={styles.consult_multi_tag_list} aria-label="선택된 상품">
            {selectedMenus.map((m) => (
              <li
                key={m.key}
                className={styles.consult_multi_tag}
                onClick={() => toggleMenuKey(m.key, false)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') toggleMenuKey(m.key, false);
                }}
                aria-label={`${m.label} 선택 해제`}
              >
                <span>{m.label}</span>
                <Icon name={EtcClose} size={24} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectionAside}

      {typeof document !== 'undefined' && mobilePickerOpen && createPortal(
        <div
          className={styles.consult_multi_sheet_overlay}
          role="presentation"
          onClick={() => setMobilePickerOpen(false)}
        >
          <div
            className={styles.consult_multi_sheet}
            role="dialog"
            aria-modal="true"
            aria-label="상품 유형 선택"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.consult_multi_sheet_head}>
              <span>문의 상품</span>
              <button
                type="button"
                className={styles.consult_multi_sheet_close}
                aria-label="닫기"
                onClick={() => onClose?.()}
              >
                <Icon name={ActionCloseType1} size={24} />
              </button>
            </div>
            <div className={styles.consult_multi_sheet_body}>
              {renderMultiSelectionGroups(CONSULT_SELECTION_NAME)}
            </div>
            <div className={styles.consult_multi_sheet_foot}>
              <Button
                type="black"
                size="large"
                disabled={selectedKeys.size === 0}
                onClick={() => setMobilePickerOpen(false)}
                className={styles.consult_multi_sheet_confirm}
                icon={false}
              >
                선택 완료
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {selectedKeys.size === 0 ? (
        <div className={styles.consult_multi_empty}>
          <div className={clsx(styles.consult_multi_empty_bubble, !showHint && styles.consult_multi_empty_bubble_out)}>
            <EventBubble pos="left" radius='medium' textSize={14}>상담을 원하는 상품들을 선택해보세요.</EventBubble>
          </div>
          <h5 className={styles.consult_multi_empty_title}>궁금한 상품을 모두 선택해 보세요</h5>
          <p className={styles.consult_multi_empty_desc}>선택한 상품의 자세한 견적을<br/>무료로 확인할 수 있습니다.</p>
        </div>
      ) : (
        <Form
          formName={POPUP_FORM_NAME_MULTI}
          leadFormNum={leadFormNum}
          leadType={POPUP_LEAD_TYPE}
          gtmItemCategory={toItemCategories(selectedMenus)}
          fields={mergedFields}
          syncValues={multiUserCountSync}
          extraData={{ productType: selectedMenus.map((m) => m.productTypeValue).filter(Boolean) }}
          agreement={agreement}
          submitLabel={submitLabelMulti}
          showTip={false}
          className={styles.form_area}
          actionAreaClassName={styles.form_action_area}
          onSuccess={() => {
            onComplete({ completeKey: mergeCompleteKey(selectedMenus) });
          }}
        />
      )}
    </div>
  );
}
